import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  cancelChannelParityJob,
  fetchChannelParityJob,
  fetchChannelParityJobHistory,
  fetchChannelParityMonitor,
  remediateParityQuantityDrift,
  startChannelParityJob,
  triggerPricingBackfill,
  triggerQuantityBackfill,
  type ChannelParityReportResponse,
  type ChannelParityJob,
  type ChannelParityMonitorResponse,
  type ChannelParityJobStatus,
  type ChannelParityChannelSuccess,
  type ChannelParityChannelError,
  type ChannelParityDriftRow,
  type PricingBackfillResponse,
  type QuantityBackfillResponse,
  type ParityQuantityRemediationResponse,
} from '../api/reports';
import { borderRadius, colors, spacing, typography, typographyFlat } from '../styles/tokens';
import { formatInstant, parseInstant } from '../utils/datetime';

const SUPPORTED_CHANNELS = ['shopify', 'manapool'];
const PARITY_JOB_POLL_MS = 5000;
const MONITOR_POLL_MS = 30000;
const MONITOR_STALE_AFTER_MINUTES = 180;
/** Conservative default while listing-grain parity is being validated in test. */
const SAFE_DEFAULT_LIMIT = '25';

function parsePositiveLimit(raw: string, fallback = 25): number {
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? Math.min(Math.floor(parsed), 5000) : fallback;
}

export const ReportsChannelParityPage: React.FC = () => {
  const [report, setReport] = useState<ChannelParityReportResponse | null>(null);
  const [selectedChannels, setSelectedChannels] = useState<string[]>(['shopify', 'manapool']);
  const [activeJob, setActiveJob] = useState<ChannelParityJob | null>(null);
  const [jobHistory, setJobHistory] = useState<ChannelParityJob[]>([]);
  const [monitor, setMonitor] = useState<ChannelParityMonitorResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [backfillLimit, setBackfillLimit] = useState<string>(SAFE_DEFAULT_LIMIT);
  const [backfillScope, setBackfillScope] = useState<'overrides' | 'all_active'>('overrides');
  const [backfillLoading, setBackfillLoading] = useState(false);
  const [backfillError, setBackfillError] = useState<string | null>(null);
  const [backfillResult, setBackfillResult] = useState<PricingBackfillResponse | null>(null);
  const [lastPricingDryRunKey, setLastPricingDryRunKey] = useState<string | null>(null);
  const [quantityBackfillLimit, setQuantityBackfillLimit] = useState<string>(SAFE_DEFAULT_LIMIT);
  const [quantityBackfillScope, setQuantityBackfillScope] = useState<'sellable_only' | 'all_active'>('sellable_only');
  const [quantityBackfillLoading, setQuantityBackfillLoading] = useState(false);
  const [quantityBackfillError, setQuantityBackfillError] = useState<string | null>(null);
  const [quantityBackfillResult, setQuantityBackfillResult] = useState<QuantityBackfillResponse | null>(null);
  const [lastQuantityDryRunKey, setLastQuantityDryRunKey] = useState<string | null>(null);
  const [remediationChannel, setRemediationChannel] = useState<'shopify' | 'manapool'>('manapool');
  const [remediationLimit, setRemediationLimit] = useState<string>(SAFE_DEFAULT_LIMIT);
  const [remediationLoading, setRemediationLoading] = useState(false);
  const [remediationError, setRemediationError] = useState<string | null>(null);
  const [remediationResult, setRemediationResult] = useState<ParityQuantityRemediationResponse | null>(null);
  const [lastRemediationDryRunKey, setLastRemediationDryRunKey] = useState<string | null>(null);
  const [safeMode, setSafeMode] = useState(true);
  const [jobPollAt, setJobPollAt] = useState<string | null>(null);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [monitorUnavailable, setMonitorUnavailable] = useState<string | null>(null);
  const [cancelLoading, setCancelLoading] = useState(false);

  const refreshHistory = useCallback(async () => {
    const historyResponse = await fetchChannelParityJobHistory(20);
    setJobHistory(historyResponse.jobs);
    return historyResponse.jobs;
  }, []);

  const refreshMonitor = useCallback(async (): Promise<ChannelParityMonitorResponse | null> => {
    try {
      const monitorResponse = await fetchChannelParityMonitor({
        stale_after_minutes: MONITOR_STALE_AFTER_MINUTES,
      });
      setMonitor(monitorResponse);
      setMonitorUnavailable(null);
      return monitorResponse;
    } catch (err) {
      const status = getHttpStatus(err);
      if (status === 404) {
        setMonitorUnavailable(
          'Drift monitor API returned 404 — this usually means the frontend is ahead of the API deploy. Job history and Start Parity Check still work.',
        );
        return null;
      }
      // Non-404: keep last-known monitor; optional soft note without failing the page.
      return null;
    }
  }, []);

  const startJob = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const job = await startChannelParityJob({
        channels: selectedChannels.join(','),
      });
      setActiveJob(job);
      setJobPollAt(null);
      await refreshHistory();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start channel parity check');
    } finally {
      setIsLoading(false);
    }
  }, [refreshHistory, selectedChannels]);

  const cancelJob = useCallback(async () => {
    if (!activeJob || (activeJob.status !== 'queued' && activeJob.status !== 'running')) {
      return;
    }
    const shouldProceed = window.confirm(
      `Cancel parity job ${activeJob.job_id}?\n\nThis marks the job failed. If a worker is still hung, restart app/scheduler after cancel.`,
    );
    if (!shouldProceed) {
      return;
    }

    try {
      setCancelLoading(true);
      setError(null);
      const updated = await cancelChannelParityJob(activeJob.job_id);
      setActiveJob(updated);
      await refreshHistory();
      void refreshMonitor();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel channel parity job');
    } finally {
      setCancelLoading(false);
    }
  }, [activeJob, refreshHistory, refreshMonitor]);

  useEffect(() => {
    let cancelled = false;

    const bootstrap = async () => {
      try {
        setError(null);
        const [jobs] = await Promise.all([
          refreshHistory(),
          refreshMonitor(), // failures are handled inside; must not block job history
        ]);
        if (cancelled || jobs.length === 0) {
          return;
        }

        const inFlight = jobs.find((job) => job.status === 'queued' || job.status === 'running');
        const latest = inFlight ?? jobs[0];
        setActiveJob(latest);
        if (latest.result) {
          setReport(latest.result);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load channel parity job history');
        }
      }
    };

    void bootstrap();
    return () => {
      cancelled = true;
    };
  }, [refreshHistory, refreshMonitor]);

  useEffect(() => {
    if (monitorUnavailable) {
      return;
    }

    let cancelled = false;
    const intervalId = window.setInterval(() => {
      void (async () => {
        const response = await refreshMonitor();
        if (cancelled || response) {
          return;
        }
        // refreshMonitor already recorded 404 / soft failure
      })();
    }, MONITOR_POLL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [monitorUnavailable, refreshMonitor]);

  const activeJobId = activeJob?.job_id ?? null;
  const activeJobStatus = activeJob?.status ?? null;

  useEffect(() => {
    if (!activeJobId || (activeJobStatus !== 'queued' && activeJobStatus !== 'running')) {
      return;
    }

    let cancelled = false;

    const pollOnce = async () => {
      try {
        const updatedJob = await fetchChannelParityJob(activeJobId);
        if (cancelled) {
          return;
        }
        setJobPollAt(new Date().toISOString());
        setActiveJob(updatedJob);
        if (updatedJob.result) {
          setReport(updatedJob.result);
        }
        if (updatedJob.status === 'success' || updatedJob.status === 'failed') {
          await refreshHistory();
          void refreshMonitor();
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to poll channel parity job status');
        }
      }
    };

    void pollOnce();
    const intervalId = window.setInterval(() => {
      void pollOnce();
    }, PARITY_JOB_POLL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [activeJobId, activeJobStatus, refreshHistory, refreshMonitor]);

  useEffect(() => {
    if (!activeJob || (activeJob.status !== 'queued' && activeJob.status !== 'running')) {
      return;
    }
    setNowMs(Date.now());
    const tickId = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(tickId);
  }, [activeJob?.job_id, activeJob?.status]);

  const handleApplyFilters = async (event: React.FormEvent) => {
    event.preventDefault();
    await startJob();
  };

  const toggleChannel = (channel: string) => {
    setSelectedChannels((prev) =>
      prev.includes(channel) ? prev.filter((c) => c !== channel) : [...prev, channel],
    );
  };

  const remediationRunKey = `${remediationChannel}:${parsePositiveLimit(remediationLimit)}`;
  const pricingRunKey = `${backfillScope}:${parsePositiveLimit(backfillLimit)}`;
  const quantityRunKey = `${quantityBackfillScope}:${parsePositiveLimit(quantityBackfillLimit)}`;
  const remediationReady = lastRemediationDryRunKey === remediationRunKey;
  const pricingReady = lastPricingDryRunKey === pricingRunKey;
  const quantityReady = lastQuantityDryRunKey === quantityRunKey;

  const runPricingBackfill = useCallback(async (dryRun: boolean) => {
    setBackfillLoading(true);
    setBackfillError(null);

    const limit = parsePositiveLimit(backfillLimit);
    const runKey = `${backfillScope}:${limit}`;

    if (!dryRun) {
      if (safeMode && lastPricingDryRunKey !== runKey) {
        setBackfillLoading(false);
        setBackfillError('Run a dry run first for this scope/limit before executing price backfill.');
        return;
      }
      const shouldProceed = window.confirm(
        `Execute pricing backfill now?\n\nScope: ${backfillScope}\nLimit: ${limit}\n\nThis emits pricing outbox events to enabled channels.`,
      );
      if (!shouldProceed) {
        setBackfillLoading(false);
        return;
      }
    }

    try {
      const result = await triggerPricingBackfill({
        dry_run: dryRun,
        limit,
        scope: backfillScope,
      });
      setBackfillResult(result);
      if (dryRun) {
        setLastPricingDryRunKey(runKey);
      }
    } catch (err) {
      setBackfillError(err instanceof Error ? err.message : 'Failed to trigger pricing backfill');
    } finally {
      setBackfillLoading(false);
    }
  }, [backfillLimit, backfillScope, lastPricingDryRunKey, safeMode]);

  const runQuantityBackfill = useCallback(async (dryRun: boolean) => {
    setQuantityBackfillLoading(true);
    setQuantityBackfillError(null);

    const limit = parsePositiveLimit(quantityBackfillLimit);
    const runKey = `${quantityBackfillScope}:${limit}`;

    if (!dryRun) {
      if (safeMode && lastQuantityDryRunKey !== runKey) {
        setQuantityBackfillLoading(false);
        setQuantityBackfillError('Run a dry run first for this scope/limit before executing quantity backfill.');
        return;
      }
      const shouldProceed = window.confirm(
        `Execute quantity backfill now?\n\nScope: ${quantityBackfillScope}\nLimit: ${limit}\n\nThis emits inventory.updated outbox events (no pricing side effects).`,
      );
      if (!shouldProceed) {
        setQuantityBackfillLoading(false);
        return;
      }
    }

    try {
      const result = await triggerQuantityBackfill({
        dry_run: dryRun,
        limit,
        scope: quantityBackfillScope,
      });
      setQuantityBackfillResult(result);
      if (dryRun) {
        setLastQuantityDryRunKey(runKey);
      }
    } catch (err) {
      setQuantityBackfillError(err instanceof Error ? err.message : 'Failed to trigger quantity backfill');
    } finally {
      setQuantityBackfillLoading(false);
    }
  }, [quantityBackfillLimit, quantityBackfillScope, lastQuantityDryRunKey, safeMode]);

  const runParityRemediation = useCallback(async (dryRun: boolean) => {
    setRemediationLoading(true);
    setRemediationError(null);

    const limit = parsePositiveLimit(remediationLimit);
    const runKey = `${remediationChannel}:${limit}`;

    if (!dryRun) {
      if (safeMode && lastRemediationDryRunKey !== runKey) {
        setRemediationLoading(false);
        setRemediationError('Run a dry run first for this channel/limit before executing remediation.');
        return;
      }
      const shouldProceed = window.confirm(
        `Apply listing-grain remediation to ${remediationChannel} now?\n\nLimit: ${limit}\n\nThis emits inventory.updated outbox events only for drifted variants (condition/finish/language precise).`,
      );
      if (!shouldProceed) {
        setRemediationLoading(false);
        return;
      }
    }

    try {
      const result = await remediateParityQuantityDrift({
        channel: remediationChannel,
        dry_run: dryRun,
        limit,
      });
      setRemediationResult(result);
      if (dryRun) {
        setLastRemediationDryRunKey(runKey);
      }
      await Promise.all([refreshHistory(), refreshMonitor()]);
    } catch (err) {
      setRemediationError(err instanceof Error ? err.message : 'Failed to remediate parity drift');
    } finally {
      setRemediationLoading(false);
    }
  }, [remediationLimit, remediationChannel, lastRemediationDryRunKey, safeMode, refreshHistory, refreshMonitor]);

  const overallPass = report?.parity_pass ?? false;
  const isActiveRunning = activeJob?.status === 'queued' || activeJob?.status === 'running';
  const jobElapsedLabel = isActiveRunning && activeJob
    ? formatElapsed(
      nowMs - (parseInstant(activeJob.started_at ?? activeJob.created_at)?.getTime() ?? nowMs),
    )
    : null;

  return (
    <div style={styles.container}>
      <style>{`
        @keyframes parity-job-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.35; transform: scale(0.85); }
        }
      `}</style>
      {isActiveRunning && activeJob && (
        <div style={styles.runningBanner} role="status" aria-live="polite">
          <div style={styles.runningBannerPulse} aria-hidden />
          <div style={styles.runningBannerBody}>
            <strong style={styles.runningBannerTitle}>
              Parity job {activeJob.status}
            </strong>
            <span style={styles.runningBannerMeta}>
              {activeJob.requested_channels.join(', ')} · elapsed {jobElapsedLabel}
              {jobPollAt ? ` · last polled ${formatTimestamp(jobPollAt)}` : ' · polling…'}
            </span>
            <span style={styles.runningBannerMeta}>
              Job {activeJob.job_id}
            </span>
          </div>
          <button
            type="button"
            style={styles.cancelBannerButton}
            onClick={() => void cancelJob()}
            disabled={cancelLoading}
          >
            {cancelLoading ? 'Cancelling…' : 'Cancel job'}
          </button>
          <span style={statusPillStyle(activeJob.status)}>{activeJob.status}</span>
        </div>
      )}

      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Reports: Channel Parity</h1>
          <p style={styles.subtitle}>
            Live listing-grain comparison of SyncBase sellable copies against each channel
            (product + condition + finish + language). Detect first, dry-run second, execute last.
            Manapool may briefly show higher totals after an order until their seller inventory
            deducts — compare copy delta to recent Manapool allocations, not the full unshipped pool.
          </p>
        </div>
        <div style={styles.headerLinks}>
          <Link to="/channels/shopify" style={styles.linkButton}>Shopify Channel</Link>
          <Link to="/channels/manapool" style={styles.linkButton}>Manapool Channel</Link>
          <Link to="/channels/tcgplayer" style={styles.linkButton}>TCGPlayer Channel</Link>
          <Link to="/dashboards" style={styles.linkButton}>Channel Operations</Link>
          <Link to="/reports/shipped-shortfall-with-stock" style={styles.linkButton}>
            Shipped Shortfall
          </Link>
          <Link to="/reports/sync-reliability" style={styles.linkButton}>Sync Reliability</Link>
        </div>
      </div>

      <section style={styles.safetyBanner}>
        <div style={styles.safetyBannerHeader}>
          <strong style={styles.safetyBannerTitle}>Safe testing mode</strong>
          <label style={styles.safeModeToggle}>
            <input
              type="checkbox"
              checked={safeMode}
              onChange={(event) => setSafeMode(event.target.checked)}
            />
            Require dry run before execute
          </label>
        </div>
        <ol style={styles.safetySteps}>
          <li>Start a parity check (read-only — no channel writes).</li>
          <li>Review drift rows by condition / finish / language.</li>
          <li>Dry-run remediation or backfill with a small limit (default {SAFE_DEFAULT_LIMIT}).</li>
          <li>Only then execute — confirm dialog still required.</li>
        </ol>
        <p style={styles.safetyNote}>
          Remediation is listing-precise. Broad price/quantity backfills can fan out more widely — keep limits low while validating.
        </p>
      </section>
      {monitorUnavailable && (
        <div style={styles.monitorWarnBanner} role="status">
          {monitorUnavailable}
        </div>
      )}

      <form style={styles.filtersRow} onSubmit={handleApplyFilters}>
        <div style={styles.filterLabel}>
          <span style={styles.filterLabelText}>Channels</span>
          <div style={styles.checkboxGroup}>
            {SUPPORTED_CHANNELS.map((channel) => (
              <label key={channel} style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={selectedChannels.includes(channel)}
                  onChange={() => toggleChannel(channel)}
                  style={styles.checkbox}
                />
                {capitalize(channel)}
              </label>
            ))}
          </div>
        </div>

        <div style={styles.actions}>
          <button type="submit" style={styles.primaryButton} disabled={isLoading || isActiveRunning || selectedChannels.length === 0}>
            {isLoading ? 'Starting...' : isActiveRunning ? 'Running...' : 'Start Parity Check'}
          </button>
          {isActiveRunning && (
            <button
              type="button"
              style={styles.dangerButton}
              onClick={() => void cancelJob()}
              disabled={cancelLoading}
            >
              {cancelLoading ? 'Cancelling…' : 'Cancel job'}
            </button>
          )}
          <span style={styles.jobIdleHint}>
            {isActiveRunning
              ? `In progress · ${jobElapsedLabel}`
              : 'Idle — no parity job running'}
          </span>
        </div>
      </form>

      {error && <div style={styles.error}>{error}</div>}

      {report && (
        <>
          <div style={overallBannerStyle(overallPass)}>
            <span style={styles.overallBannerIcon}>{overallPass ? '✓' : '✗'}</span>
            <div>
              <strong style={styles.overallBannerTitle}>
                {overallPass ? 'All channels in inventory parity' : 'Inventory drift detected'}
              </strong>
              <span style={styles.overallBannerMeta}>
                {' '}— checked {report.requested_channels.join(', ')} · generated {formatTimestamp(report.generated_at)}
              </span>
            </div>
          </div>

          <div style={styles.channelGrid}>
            {report.channels.map((channelResult) =>
              channelResult.status === 'success'
                ? <ChannelSuccessCard key={channelResult.channel} result={channelResult} />
                : <ChannelErrorCard key={channelResult.channel} result={channelResult} />,
            )}
          </div>
        </>
      )}

      {activeJob && !isActiveRunning && (
        <section style={styles.card}>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>Latest Job</h2>
            <span style={statusPillStyle(activeJob.status)}>{activeJob.status}</span>
          </div>
          <p style={styles.channelMeta}>
            Job {activeJob.job_id} · channels: {activeJob.requested_channels.join(', ')}
            {' '}· started: {formatTimestamp(activeJob.started_at ?? activeJob.created_at)}
            {' '}· finished: {formatTimestamp(activeJob.finished_at)}
            {activeJob.started_at && activeJob.finished_at
              ? ` · duration ${formatElapsed((parseInstant(activeJob.finished_at)?.getTime() ?? 0) - (parseInstant(activeJob.started_at)?.getTime() ?? 0))}`
              : ''}
          </p>
          {activeJob.error_message && <p style={styles.errorMessage}>{activeJob.error_message}</p>}
        </section>
      )}

      {monitor && (
        <>
          <section style={styles.card}>
            <div style={styles.sectionHeader}>
              <h2 style={styles.sectionTitle}>Automatic Drift Monitor</h2>
              <span style={monitorStatusPillStyle(monitor.monitor_status)}>
                {monitor.monitor_status.replace('_', ' ')}
              </span>
            </div>
            <p style={styles.channelMeta}>
              Last check: {formatTimestamp(monitor.last_checked_at)} · age: {formatAgeMinutes(monitor.age_minutes)} · drift: {monitor.total_drift_count.toLocaleString()}
            </p>
            {monitor.manapool_uuid_verification && (
              <p style={styles.channelMeta}>
                Manapool FO/NF stamps: missing {monitor.manapool_uuid_verification.missing_stamp ?? 0}
                {' · '}
                same-UUID conflicts {monitor.manapool_uuid_verification.same_uuid_conflict ?? 0}
                {' · '}
                distinct OK {monitor.manapool_uuid_verification.distinct_ok ?? 0}
                {monitor.manapool_uuid_verification.safe_for_outbound === false
                  ? ' · outbound remediation blocked (shared UUID)'
                  : ''}
              </p>
            )}
            {monitor.latest_job && (
              <p style={styles.channelMeta}>
                Latest job: {monitor.latest_job.job_id} ({monitor.latest_job.status}) · channels: {monitor.latest_job.requested_channels.join(', ')}
              </p>
            )}
            {monitor.scheduler_status.status && (
              <p style={styles.snapshotMeta}>
                Scheduler status: {monitor.scheduler_status.status} · last run {formatTimestamp(monitor.scheduler_status.last_run_at)}
              </p>
            )}
          </section>

          <section style={styles.card}>
            <div style={styles.sectionHeader}>
              <h2 style={styles.sectionTitle}>API-Verified Quantity Drift Remediation</h2>
              <span style={styles.metaText}>
                Live parity → targeted inventory.updated for drifted listings only
              </span>
            </div>

          <div style={styles.backfillControls}>
            <label style={styles.filterLabel}>
              <span style={styles.filterLabelText}>Channel</span>
              <select
                value={remediationChannel}
                onChange={(event) => {
                  setRemediationChannel(event.target.value as 'shopify' | 'manapool');
                  setLastRemediationDryRunKey(null);
                }}
                style={styles.selectInput}
              >
                <option value="manapool">Manapool</option>
                <option value="shopify">Shopify</option>
              </select>
            </label>

            <label style={styles.filterLabel}>
              <span style={styles.filterLabelText}>Limit</span>
              <input
                type="number"
                min={1}
                max={5000}
                value={remediationLimit}
                onChange={(event) => {
                  setRemediationLimit(event.target.value);
                  setLastRemediationDryRunKey(null);
                }}
                style={styles.numberInput}
              />
            </label>

            <div style={styles.actions}>
              <button
                type="button"
                style={styles.secondaryButton}
                disabled={remediationLoading}
                onClick={() => void runParityRemediation(true)}
              >
                {remediationLoading ? 'Running...' : 'Dry Run'}
              </button>
              <button
                type="button"
                style={styles.dangerButton}
                disabled={remediationLoading || (safeMode && !remediationReady)}
                onClick={() => void runParityRemediation(false)}
                title={safeMode && !remediationReady ? 'Complete a dry run for this channel/limit first' : undefined}
              >
                {remediationLoading ? 'Running...' : 'Execute Remediation'}
              </button>
            </div>
          </div>
          <p style={styles.metaText}>
            {safeMode
              ? remediationReady
                ? `Dry run ready for ${remediationRunKey}. Execute is unlocked.`
                : `Safety gate on: dry-run ${remediationRunKey} before execute.`
              : 'Safe mode off: execute is allowed without a prior dry run (confirm still required).'}
          </p>

          {remediationError && <p style={styles.errorMessage}>{remediationError}</p>}
          {remediationResult && (
            <ActionResultPanel
              dryRun={remediationResult.dry_run}
              summary={`${remediationResult.dry_run ? 'Dry run' : 'Executed'} · drift rows ${remediationResult.drift_rows_seen} · variants ${remediationResult.variant_ids_targeted} · events ${remediationResult.events_emitted}`}
              details={[
                `Channel: ${remediationResult.channel}`,
                `Targets: ${remediationResult.targets.join(', ') || '(none)'}`,
                remediationResult.parity_source
                  ? `Parity source: ${remediationResult.parity_source}${
                      remediationResult.parity_job_id
                        ? ` (${remediationResult.parity_job_id}${
                            remediationResult.parity_generated_at
                              ? ` @ ${formatTimestamp(remediationResult.parity_generated_at)}`
                              : ''
                          })`
                        : ''
                    }`
                  : null,
                typeof remediationResult.total_drift_count === 'number'
                  ? `Live total drift count: ${remediationResult.total_drift_count}`
                  : null,
                remediationResult.verification
                  ? `FO/NF stamps: missing ${remediationResult.verification.missing_stamp ?? 0}, conflicts ${remediationResult.verification.same_uuid_conflict ?? 0}`
                  : null,
                typeof remediationResult.skipped_missing_stamp_count === 'number'
                  && remediationResult.skipped_missing_stamp_count > 0
                  ? `Skipped missing-stamp rows: ${remediationResult.skipped_missing_stamp_count}`
                  : null,
                typeof remediationResult.skipped_shipped_shortfall_count === 'number'
                  && remediationResult.skipped_shipped_shortfall_count > 0
                  ? `Skipped shipped-shortfall-with-stock variants: ${remediationResult.skipped_shipped_shortfall_count}`
                  : null,
                remediationResult.message || null,
              ]}
            >
              {Array.isArray(remediationResult.sample_actionable_rows) && remediationResult.sample_actionable_rows.length > 0 && (
                <div style={styles.sampleTargets}>
                  <p style={styles.filterLabelText}>Sample actionable listings</p>
                  <ul style={styles.sampleTargetList}>
                    {remediationResult.sample_actionable_rows.slice(0, 8).map((row, index) => (
                      <li key={`${row.variant_id || row.key || index}`}>
                        {[row.product_name, row.condition, row.finish, row.language]
                          .filter(Boolean)
                          .join(' · ') || row.key || 'listing'}
                        {typeof row.expected_quantity === 'number' && typeof row.channel_quantity === 'number'
                          ? ` — qty ${row.expected_quantity} → ${row.channel_quantity}`
                          : ''}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {Array.isArray(remediationResult.sample_skipped_missing_stamp_rows)
                && remediationResult.sample_skipped_missing_stamp_rows.length > 0 && (
                <div style={styles.sampleTargets}>
                  <p style={styles.filterLabelText}>Skipped (missing FO/NF Manapool stamp)</p>
                  <ul style={styles.sampleTargetList}>
                    {remediationResult.sample_skipped_missing_stamp_rows.slice(0, 5).map((row, index) => (
                      <li key={`skip-${row.variant_id || row.key || index}`}>
                        {[row.product_name, row.condition, row.finish, row.language]
                          .filter(Boolean)
                          .join(' · ') || row.key || 'listing'}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {Array.isArray(remediationResult.sample_skipped_shipped_shortfall_rows)
                && remediationResult.sample_skipped_shipped_shortfall_rows.length > 0 && (
                <div style={styles.sampleTargets}>
                  <p style={styles.filterLabelText}>Skipped (shipped shortfall + remaining stock)</p>
                  <ul style={styles.sampleTargetList}>
                    {remediationResult.sample_skipped_shipped_shortfall_rows.slice(0, 5).map((row, index) => (
                      <li key={`ship-skip-${row.variant_id || row.key || index}`}>
                        {row.card_name || row.key || 'listing'}
                        {row.order_external_id ? ` · order ${row.order_external_id}` : ''}
                        {typeof row.shortfall_qty === 'number' ? ` · shortfall ${row.shortfall_qty}` : ''}
                        {typeof row.in_stock_now === 'number' ? ` · in stock ${row.in_stock_now}` : ''}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </ActionResultPanel>
          )}
          </section>        </>
      )}

      <section style={styles.card}>
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>Broad Backfills (advanced)</h2>
          <span style={styles.metaText}>Prefer listing remediation above; these fan out by scope/limit</span>
        </div>

        <div style={styles.advancedBlock}>
          <h3 style={styles.subsectionTitle}>Price Mismatch Backfill</h3>
          <div style={styles.backfillControls}>
            <label style={styles.filterLabel}>
              <span style={styles.filterLabelText}>Limit</span>
              <input
                type="number"
                min={1}
                max={5000}
                value={backfillLimit}
                onChange={(event) => {
                  setBackfillLimit(event.target.value);
                  setLastPricingDryRunKey(null);
                }}
                style={styles.numberInput}
              />
            </label>

            <label style={styles.filterLabel}>
              <span style={styles.filterLabelText}>Scope</span>
              <select
                value={backfillScope}
                onChange={(event) => {
                  setBackfillScope(event.target.value as 'overrides' | 'all_active');
                  setLastPricingDryRunKey(null);
                }}
                style={styles.selectInput}
              >
                <option value="overrides">Override variants only</option>
                <option value="all_active">All active variants</option>
              </select>
            </label>

            <div style={styles.actions}>
              <button
                type="button"
                style={styles.secondaryButton}
                disabled={backfillLoading}
                onClick={() => void runPricingBackfill(true)}
              >
                {backfillLoading ? 'Working...' : 'Dry Run'}
              </button>
              <button
                type="button"
                style={styles.dangerButton}
                disabled={backfillLoading || (safeMode && !pricingReady)}
                onClick={() => void runPricingBackfill(false)}
              >
                {backfillLoading ? 'Working...' : 'Execute Backfill'}
              </button>
            </div>
          </div>
          <p style={styles.metaText}>
            {safeMode
              ? pricingReady
                ? `Dry run ready for ${pricingRunKey}.`
                : `Dry-run ${pricingRunKey} before execute.`
              : 'Safe mode off — confirm still required on execute.'}
          </p>
          {backfillError && <p style={styles.errorMessage}>{backfillError}</p>}
          {backfillResult && (
            <ActionResultPanel
              dryRun={backfillResult.dry_run}
              summary={`${backfillResult.dry_run ? 'Dry run' : 'Executed'} · ${backfillResult.events_emitted} events · ${backfillResult.total_found} variants · scope ${backfillResult.scope}`}
              details={[
                `Targets: ${backfillResult.targets.length > 0 ? backfillResult.targets.join(', ') : '(none)'}`,
                backfillResult.message || null,
              ]}
            />
          )}
        </div>

        <div style={styles.advancedBlock}>
          <h3 style={styles.subsectionTitle}>Quantity Mismatch Backfill</h3>
          <p style={styles.metaText}>Emits inventory.updated only (no pricing side effects). Prefer API-verified remediation when possible.</p>
          <div style={styles.backfillControls}>
            <label style={styles.filterLabel}>
              <span style={styles.filterLabelText}>Limit</span>
              <input
                type="number"
                min={1}
                max={5000}
                value={quantityBackfillLimit}
                onChange={(event) => {
                  setQuantityBackfillLimit(event.target.value);
                  setLastQuantityDryRunKey(null);
                }}
                style={styles.numberInput}
              />
            </label>

            <label style={styles.filterLabel}>
              <span style={styles.filterLabelText}>Scope</span>
              <select
                value={quantityBackfillScope}
                onChange={(event) => {
                  setQuantityBackfillScope(event.target.value as 'sellable_only' | 'all_active');
                  setLastQuantityDryRunKey(null);
                }}
                style={styles.selectInput}
              >
                <option value="sellable_only">Sellable variants only</option>
                <option value="all_active">All active variants</option>
              </select>
            </label>

            <div style={styles.actions}>
              <button
                type="button"
                style={styles.secondaryButton}
                disabled={quantityBackfillLoading}
                onClick={() => void runQuantityBackfill(true)}
              >
                {quantityBackfillLoading ? 'Working...' : 'Dry Run'}
              </button>
              <button
                type="button"
                style={styles.dangerButton}
                disabled={quantityBackfillLoading || (safeMode && !quantityReady)}
                onClick={() => void runQuantityBackfill(false)}
              >
                {quantityBackfillLoading ? 'Working...' : 'Execute Backfill'}
              </button>
            </div>
          </div>
          <p style={styles.metaText}>
            {safeMode
              ? quantityReady
                ? `Dry run ready for ${quantityRunKey}.`
                : `Dry-run ${quantityRunKey} before execute.`
              : 'Safe mode off — confirm still required on execute.'}
          </p>
          {quantityBackfillError && <p style={styles.errorMessage}>{quantityBackfillError}</p>}
          {quantityBackfillResult && (
            <ActionResultPanel
              dryRun={quantityBackfillResult.dry_run}
              summary={`${quantityBackfillResult.dry_run ? 'Dry run' : 'Executed'} · ${quantityBackfillResult.events_emitted} events · ${quantityBackfillResult.total_found} variants · scope ${quantityBackfillResult.scope}`}
              details={[
                `Targets: ${quantityBackfillResult.targets.length > 0 ? quantityBackfillResult.targets.join(', ') : '(none)'}`,
                typeof quantityBackfillResult.skipped_shipped_shortfall_count === 'number'
                  && quantityBackfillResult.skipped_shipped_shortfall_count > 0
                  ? `Skipped shipped-shortfall-with-stock variants: ${quantityBackfillResult.skipped_shipped_shortfall_count}`
                  : null,
                quantityBackfillResult.message || null,
              ]}
            />
          )}
        </div>
      </section>

      <section style={styles.card}>
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>Previous Jobs</h2>
          <span style={styles.metaText}>{jobHistory.length} rows</span>
        </div>
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Created</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Channels</th>
                <th style={styles.th}>Finished</th>
                <th style={styles.th}>Duration</th>
                <th style={styles.th}>Parity</th>
              </tr>
            </thead>
            <tbody>
              {jobHistory.length === 0 && (
                <tr>
                  <td style={styles.emptyCell} colSpan={6}>No parity jobs recorded yet.</td>
                </tr>
              )}
              {jobHistory.map((job) => {
                const rowRunning = job.status === 'queued' || job.status === 'running';
                const durationMs =
                  typeof job.duration_seconds === 'number'
                    ? job.duration_seconds * 1000
                    : job.started_at && job.finished_at
                      ? (parseInstant(job.finished_at)?.getTime() ?? 0) - (parseInstant(job.started_at)?.getTime() ?? 0)
                      : null;
                return (
                  <tr
                    key={job.job_id}
                    style={{
                      ...(rowRunning ? styles.historyRowRunning : {}),
                      cursor: job.result ? 'pointer' : undefined,
                    }}
                    onClick={() => {
                      if (job.result) {
                        setReport(job.result);
                        setActiveJob(job);
                      }
                    }}
                  >
                    <td style={styles.td}>{formatTimestamp(job.created_at)}</td>
                    <td style={styles.td}><span style={statusPillStyle(job.status)}>{job.status}</span></td>
                    <td style={styles.td}>{job.requested_channels.join(', ')}</td>
                    <td style={styles.td}>
                      {rowRunning
                        ? `in progress · ${formatElapsed(nowMs - (parseInstant(job.started_at ?? job.created_at)?.getTime() ?? nowMs))}`
                        : formatTimestamp(job.finished_at)}
                    </td>
                    <td style={styles.td}>
                      {rowRunning
                        ? '…'
                        : durationMs != null
                          ? formatElapsed(durationMs)
                          : '-'}
                    </td>
                    <td style={styles.td}>
                      {job.result ? (job.result.parity_pass ? 'pass' : 'drift') : rowRunning ? '…' : '-'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

function ChannelSuccessCard({ result }: { result: ChannelParityChannelSuccess }) {
  const [showDrift, setShowDrift] = useState(true);
  const inventoryPass = result.inventory_parity_pass ?? result.parity_pass;
  const identityPass = result.identity_parity_pass ?? !result.likely_identity_skew;
  const inventoryDrift = result.inventory_drift_count ?? result.total_drift_count;
  const identityDrift = result.identity_drift_count ?? result.root_diff_same_suffix_count ?? 0;

  return (
    <section style={styles.channelCard}>
      <div style={styles.channelCardHeader}>
        <div style={styles.channelCardTitleRow}>
          <span style={channelPassBadgeStyle(inventoryPass)}>
            {inventoryPass ? '✓ Inventory OK' : '✗ Inventory Drift'}
          </span>
          <span style={channelPassBadgeStyle(identityPass)}>
            {identityPass ? '✓ Identity OK' : '✗ Identity Drift'}
          </span>
          <h2 style={styles.channelCardTitle}>{capitalize(result.channel)}</h2>
        </div>
        <span style={styles.channelMeta}>
          {result.snapshot_meta.fetched_count.toLocaleString()} items fetched
          {!result.snapshot_meta.is_complete && (
            <span style={styles.incompleteWarning}> · snapshot incomplete</span>
          )}
        </span>
      </div>

      <div style={styles.metricsRow}>
        <Metric label="Expected listings" value={result.total_expected_items.toLocaleString()} />
        <Metric label="In Channel listings" value={result.total_channel_items.toLocaleString()} />
        <Metric label="In Sync" value={result.in_sync_count.toLocaleString()} accent={inventoryPass ? 'success' : undefined} />
        <Metric
          label="Inventory Drift"
          value={inventoryDrift.toLocaleString()}
          accent={inventoryDrift > 0 ? 'error' : undefined}
        />
        <Metric
          label="Identity Drift"
          value={identityDrift.toLocaleString()}
          accent={identityDrift > 0 ? 'warning' : undefined}
        />
      </div>

      <div style={styles.metricsRow}>
        <Metric label="SyncBase copies" value={(result.syncbase_sellable_copies ?? 0).toLocaleString()} />
        <Metric label="Channel copies" value={(result.channel_copies ?? 0).toLocaleString()} />
        <Metric
          label="Copy delta"
          value={`${(result.copy_delta ?? 0) > 0 ? '+' : ''}${(result.copy_delta ?? 0).toLocaleString()}`}
          accent={(result.copy_delta ?? 0) !== 0 ? 'warning' : undefined}
        />
        {typeof result.recent_manapool_allocated_copies === 'number' && (
          <Metric label="Recent MP allocated" value={result.recent_manapool_allocated_copies.toLocaleString()} />
        )}
      </div>

      {typeof result.manapool_unshipped_allocated_copies === 'number' && (
        <p style={styles.identitySkewNote}>
          Manapool lag context: {result.manapool_unshipped_allocated_copies.toLocaleString()} sold copies
          still allocated on open Manapool orders
          {typeof result.recent_manapool_allocated_copies === 'number' && (
            <> · {result.recent_manapool_allocated_copies.toLocaleString()} recent/early-status</>
          )}
          . A positive copy delta near recent allocated is usually post-order seller-inventory lag,
          not missing SyncBase stock.
        </p>
      )}

      {((result.root_diff_same_suffix_count ?? 0) > 0) && (
        <p style={styles.identitySkewNote}>
          Identity skew: {(result.root_diff_same_suffix_count ?? 0).toLocaleString()} listing(s) share the
          same condition/finish/language suffix but different UUID roots (often DFC face ids or legacy
          channel SKUs). Inventory pass ignores these; compare uses SyncBase MTGJSON composites.
          {typeof result.true_missing_estimate === 'number' && typeof result.true_extra_estimate === 'number' && (
            <>
              {' '}True missing ≈ {result.true_missing_estimate.toLocaleString()}, true extra ≈{' '}
              {result.true_extra_estimate.toLocaleString()}.
            </>
          )}
        </p>
      )}

      {(inventoryDrift > 0 || identityDrift > 0 || result.total_drift_count > 0) && (
        <div style={styles.driftBreakdown}>
          {result.missing_in_channel > 0 && (
            <DriftPill label="Missing in channel" count={result.missing_in_channel} />
          )}
          {result.extra_in_channel > 0 && (
            <DriftPill label="Extra in channel" count={result.extra_in_channel} />
          )}
          {result.quantity_mismatches > 0 && (
            <DriftPill label="Qty mismatch" count={result.quantity_mismatches} />
          )}
          {(result.root_diff_same_suffix_count ?? 0) > 0 && (
            <DriftPill label="Root diff (same suffix)" count={result.root_diff_same_suffix_count ?? 0} variant="warning" />
          )}
          {result.unmatched_keys_count > 0 && (
            <DriftPill label="Unmatched keys" count={result.unmatched_keys_count} variant="warning" />
          )}
        </div>
      )}

      {result.price_mismatches > 0 && (
        <details style={styles.priceDeferredDetails}>
          <summary style={styles.priceDeferredSummary}>
            Price mismatches ({result.price_mismatches.toLocaleString()}) — not synced yet, ignored for pass/fail
          </summary>
          <p style={styles.channelMeta}>
            Price sync is not active. These differences are diagnostic only and do not affect
            inventory drift or parity pass.
          </p>
        </details>
      )}

      {result.sample_drift.length > 0 && (
        <div style={styles.sampleDriftSection}>
          <button
            type="button"
            style={styles.toggleButton}
            onClick={() => setShowDrift((prev) => !prev)}
          >
            {showDrift ? 'Hide' : 'Show'} sample inventory drift ({result.sample_drift.length} rows, highest risk first)
          </button>

          {showDrift && (
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Product</th>
                    <th style={styles.th}>Set</th>
                    <th style={styles.th}>Condition</th>
                    <th style={styles.th}>Finish</th>
                    <th style={styles.th}>Lang</th>
                    <th style={styles.th}>Drift Types</th>
                    <th style={styles.th}>Expected Qty</th>
                    <th style={styles.th}>Channel Qty</th>
                  </tr>
                </thead>
                <tbody>
                  {result.sample_drift.map((row) => (
                    <DriftRow key={row.key} row={row} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <div style={styles.snapshotMeta}>
        Snapshot: {formatTimestamp(result.snapshot_meta.started_at)} – {formatTimestamp(result.snapshot_meta.finished_at)}
        {' '}· {result.snapshot_meta.page_count} page{result.snapshot_meta.page_count !== 1 ? 's' : ''}
      </div>
    </section>
  );
}

function ChannelErrorCard({ result }: { result: ChannelParityChannelError }) {
  return (
    <section style={{ ...styles.channelCard, ...styles.channelCardError }}>
      <div style={styles.channelCardHeader}>
        <div style={styles.channelCardTitleRow}>
          <span style={channelPassBadgeStyle(false)}>✗ Error</span>
          <h2 style={styles.channelCardTitle}>{capitalize(result.channel)}</h2>
        </div>
        <span style={styles.errorTypeBadge}>{result.error_type}</span>
      </div>
      <p style={styles.errorMessage}>{result.error}</p>
      <p style={styles.channelMeta}>
        {result.total_expected_items.toLocaleString()} items expected in SyncBase
      </p>
    </section>
  );
}

function DriftRow({ row }: { row: ChannelParityDriftRow }) {
  return (
    <tr>
      <td style={styles.tdStrong} title={row.key}>
        {row.product_name ?? row.key}
      </td>
      <td style={styles.td}>{row.set_name ?? '-'}</td>
      <td style={styles.td}>{row.condition ?? '-'}</td>
      <td style={styles.td}>{row.finish ?? '-'}</td>
      <td style={styles.td}>{row.language ?? '-'}</td>
      <td style={styles.td}>
        {row.drift_types.map((t) => (
          <span key={t} style={styles.driftTypePill}>{t.replace(/_/g, ' ')}</span>
        ))}
      </td>
      <td style={styles.td}>{row.expected_quantity ?? '-'}</td>
      <td style={styles.td}>{row.channel_quantity ?? '-'}</td>
    </tr>
  );
}

function ActionResultPanel({
  dryRun,
  summary,
  details,
  children,
}: {
  dryRun: boolean;
  summary: string;
  details: Array<string | null>;
  children?: React.ReactNode;
}) {
  return (
    <div style={{
      ...styles.backfillResultBox,
      borderColor: dryRun ? colors.border : colors.error,
      backgroundColor: dryRun ? `${colors.surface}` : colors.errorLight,
    }}>
      <div style={styles.resultBadgeRow}>
        <span style={dryRun ? styles.dryRunBadge : styles.executedBadge}>
          {dryRun ? 'DRY RUN — no writes' : 'EXECUTED — events emitted'}
        </span>
      </div>
      <p style={styles.channelMeta}>{summary}</p>
      {details.filter(Boolean).map((line) => (
        <p key={line as string} style={styles.channelMeta}>{line}</p>
      ))}
      {children}
    </div>
  );
}

function Metric({ label, value, accent }: { label: string; value: string; accent?: 'success' | 'error' | 'warning' }) {
  const valueColor =
    accent === 'success' ? colors.success :
    accent === 'error' ? colors.error :
    accent === 'warning' ? colors.warning :
    colors.text;
  return (
    <div style={styles.metric}>
      <span style={styles.metricLabel}>{label}</span>
      <strong style={{ ...styles.metricValue, color: valueColor }}>{value}</strong>
    </div>
  );
}

function DriftPill({ label, count, variant = 'error' }: { label: string; count: number; variant?: 'error' | 'warning' }) {
  const bg = variant === 'warning' ? `${colors.warning}20` : `${colors.error}20`;
  const fg = variant === 'warning' ? colors.warning : colors.error;
  return (
    <span style={{ ...styles.driftPill, backgroundColor: bg, color: fg }}>
      {count} {label}
    </span>
  );
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function formatTimestamp(value: string | null): string {
  return formatInstant(value, '-');
}

function overallBannerStyle(pass: boolean): React.CSSProperties {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.md,
    padding: spacing.md,
    borderRadius: borderRadius.md,
    border: `1px solid ${pass ? colors.success : colors.error}`,
    backgroundColor: pass ? `${colors.success}15` : `${colors.error}15`,
  };
}

function channelPassBadgeStyle(pass: boolean): React.CSSProperties {
  return {
    display: 'inline-block',
    padding: `2px ${spacing.sm}`,
    borderRadius: borderRadius.sm,
    fontSize: typography.small.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
    backgroundColor: pass ? `${colors.success}20` : `${colors.error}20`,
    color: pass ? colors.success : colors.error,
  };
}

function statusPillStyle(status: ChannelParityJobStatus): React.CSSProperties {
  if (status === 'success') {
    return {
      ...styles.statusPill,
      backgroundColor: `${colors.success}20`,
      color: colors.success,
    };
  }
  if (status === 'failed') {
    return {
      ...styles.statusPill,
      backgroundColor: `${colors.error}20`,
      color: colors.error,
    };
  }
  return {
    ...styles.statusPill,
    backgroundColor: `${colors.warning}20`,
    color: colors.warning,
  };
}

function monitorStatusPillStyle(status: ChannelParityMonitorResponse['monitor_status']): React.CSSProperties {
  if (status === 'healthy') {
    return {
      ...styles.statusPill,
      backgroundColor: `${colors.success}20`,
      color: colors.success,
    };
  }
  if (status === 'drift' || status === 'stale') {
    return {
      ...styles.statusPill,
      backgroundColor: `${colors.warning}20`,
      color: colors.warning,
    };
  }
  if (status === 'failed') {
    return {
      ...styles.statusPill,
      backgroundColor: `${colors.error}20`,
      color: colors.error,
    };
  }
  return {
    ...styles.statusPill,
    backgroundColor: `${colors.border}60`,
    color: colors.textSecondary,
  };
}

function formatAgeMinutes(minutes: number | null): string {
  if (minutes === null || Number.isNaN(minutes)) {
    return '-';
  }
  if (minutes < 60) {
    return `${minutes}m`;
  }
  const hours = Math.floor(minutes / 60);
  const rem = minutes % 60;
  if (hours < 24) {
    return `${hours}h ${rem}m`;
  }
  const days = Math.floor(hours / 24);
  return `${days}d ${hours % 24}h`;
}

function formatElapsed(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) {
    return '0s';
  }
  const totalSec = Math.floor(ms / 1000);
  const hours = Math.floor(totalSec / 3600);
  const minutes = Math.floor((totalSec % 3600) / 60);
  const seconds = totalSec % 60;
  if (hours > 0) {
    return `${hours}h ${minutes}m ${seconds}s`;
  }
  if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  }
  return `${seconds}s`;
}

function getHttpStatus(err: unknown): number | null {
  if (!err || typeof err !== 'object') {
    return null;
  }
  const response = (err as { response?: { status?: number } }).response;
  return typeof response?.status === 'number' ? response.status : null;
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: spacing.lg,
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.lg,
  } as React.CSSProperties,
  runningBanner: {
    position: 'sticky',
    top: 0,
    zIndex: 20,
    display: 'flex',
    alignItems: 'center',
    gap: spacing.md,
    padding: spacing.md,
    borderRadius: borderRadius.md,
    border: `2px solid ${colors.warning}`,
    backgroundColor: colors.surface,
    boxShadow: `0 4px 16px ${colors.warning}33`,
  } as React.CSSProperties,
  runningBannerPulse: {
    width: 12,
    height: 12,
    borderRadius: '50%',
    backgroundColor: colors.warning,
    flexShrink: 0,
    animation: 'parity-job-pulse 1.2s ease-in-out infinite',
  } as React.CSSProperties,
  runningBannerBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    flex: 1,
    minWidth: 0,
  } as React.CSSProperties,
  cancelBannerButton: {
    border: `1px solid ${colors.error}`,
    borderRadius: borderRadius.sm,
    backgroundColor: colors.surface,
    color: colors.error,
    padding: `${spacing.sm} ${spacing.md}`,
    cursor: 'pointer',
    fontSize: typography.body.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
    flexShrink: 0,
  } as React.CSSProperties,
  runningBannerTitle: {
    color: colors.text,
    fontSize: typography.body.fontSize,
  } as React.CSSProperties,
  runningBannerMeta: {
    color: colors.textSecondary,
    fontSize: typography.small.fontSize,
    wordBreak: 'break-all',
  } as React.CSSProperties,
  jobIdleHint: {
    color: colors.textSecondary,
    fontSize: typography.small.fontSize,
    alignSelf: 'center',
  } as React.CSSProperties,
  monitorWarnBanner: {
    padding: spacing.md,
    borderRadius: borderRadius.md,
    border: `1px solid ${colors.warning}`,
    backgroundColor: `${colors.warning}18`,
    color: colors.text,
    fontSize: typography.small.fontSize,
  } as React.CSSProperties,
  identitySkewNote: {
    margin: 0,
    color: colors.warning,
    fontSize: typography.small.fontSize,
    lineHeight: 1.4,
  } as React.CSSProperties,
  priceDeferredDetails: {
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.sm,
    padding: `${spacing.xs} ${spacing.sm}`,
    backgroundColor: colors.surface,
  } as React.CSSProperties,
  priceDeferredSummary: {
    cursor: 'pointer',
    color: colors.textSecondary,
    fontSize: typography.small.fontSize,
  } as React.CSSProperties,
  historyRowRunning: {
    backgroundColor: `${colors.warning}18`,
  } as React.CSSProperties,
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: spacing.md,
    alignItems: 'flex-start',
    flexWrap: 'wrap',
  } as React.CSSProperties,
  title: {
    margin: 0,
    fontSize: typography.h1.fontSize,
    fontWeight: typography.h1.fontWeight,
    color: colors.text,
  } as React.CSSProperties,
  subtitle: {
    marginTop: spacing.xs,
    marginBottom: 0,
    color: colors.textSecondary,
    fontSize: typography.body.fontSize,
    maxWidth: '960px',
  } as React.CSSProperties,
  headerLinks: {
    display: 'flex',
    gap: spacing.sm,
    flexWrap: 'wrap',
  } as React.CSSProperties,
  safetyBanner: {
    padding: spacing.md,
    borderRadius: borderRadius.md,
    border: `1px solid ${colors.warning}`,
    backgroundColor: `${colors.warning}18`,
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.sm,
  } as React.CSSProperties,
  safetyBannerHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: spacing.md,
    alignItems: 'center',
    flexWrap: 'wrap',
  } as React.CSSProperties,
  safetyBannerTitle: {
    color: colors.text,
    fontSize: typography.body.fontSize,
  } as React.CSSProperties,
  safeModeToggle: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.xs,
    color: colors.text,
    fontSize: typography.small.fontSize,
    cursor: 'pointer',
  } as React.CSSProperties,
  safetySteps: {
    margin: 0,
    paddingLeft: spacing.lg,
    color: colors.textSecondary,
    fontSize: typography.small.fontSize,
    lineHeight: 1.5,
  } as React.CSSProperties,
  safetyNote: {
    margin: 0,
    color: colors.textSecondary,
    fontSize: typography.small.fontSize,
  } as React.CSSProperties,
  linkButton: {
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.sm,
    padding: `${spacing.sm} ${spacing.md}`,
    textDecoration: 'none',
    color: colors.text,
    backgroundColor: colors.surface,
    fontSize: typography.small.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
  } as React.CSSProperties,
  filtersRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: spacing.md,
    alignItems: 'end',
    padding: spacing.md,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    backgroundColor: colors.surface,
  } as React.CSSProperties,
  filterLabel: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.xs,
  } as React.CSSProperties,
  filterLabelText: {
    color: colors.text,
    fontSize: typography.small.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
  } as React.CSSProperties,
  checkboxGroup: {
    display: 'flex',
    gap: spacing.md,
  } as React.CSSProperties,
  checkboxLabel: {
    display: 'flex',
    gap: spacing.xs,
    alignItems: 'center',
    color: colors.text,
    fontSize: typography.body.fontSize,
    cursor: 'pointer',
  } as React.CSSProperties,
  checkbox: {
    cursor: 'pointer',
  } as React.CSSProperties,
  actions: {
    display: 'flex',
    gap: spacing.sm,
    alignItems: 'flex-end',
  } as React.CSSProperties,
  backfillControls: {
    display: 'flex',
    gap: spacing.md,
    alignItems: 'flex-end',
    flexWrap: 'wrap',
  } as React.CSSProperties,
  numberInput: {
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.sm,
    padding: `${spacing.xs} ${spacing.sm}`,
    minWidth: '120px',
    backgroundColor: colors.white,
    color: colors.text,
    fontSize: typography.body.fontSize,
  } as React.CSSProperties,
  selectInput: {
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.sm,
    padding: `${spacing.xs} ${spacing.sm}`,
    minWidth: '220px',
    backgroundColor: colors.white,
    color: colors.text,
    fontSize: typography.body.fontSize,
  } as React.CSSProperties,
  primaryButton: {
    border: 'none',
    borderRadius: borderRadius.sm,
    backgroundColor: colors.primary,
    color: colors.white,
    padding: `${spacing.sm} ${spacing.md}`,
    cursor: 'pointer',
    fontSize: typography.body.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
  } as React.CSSProperties,
  secondaryButton: {
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.sm,
    backgroundColor: colors.surface,
    color: colors.text,
    padding: `${spacing.sm} ${spacing.md}`,
    cursor: 'pointer',
    fontSize: typography.body.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
  } as React.CSSProperties,
  dangerButton: {
    border: 'none',
    borderRadius: borderRadius.sm,
    backgroundColor: colors.error,
    color: colors.white,
    padding: `${spacing.sm} ${spacing.md}`,
    cursor: 'pointer',
    fontSize: typography.body.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
  } as React.CSSProperties,
  advancedBlock: {
    marginTop: spacing.md,
    paddingTop: spacing.md,
    borderTop: `1px solid ${colors.border}`,
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.sm,
  } as React.CSSProperties,
  subsectionTitle: {
    margin: 0,
    fontSize: typography.h3.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
    color: colors.text,
  } as React.CSSProperties,
  resultBadgeRow: {
    marginBottom: spacing.xs,
  } as React.CSSProperties,
  dryRunBadge: {
    display: 'inline-block',
    padding: `${spacing.xs} ${spacing.sm}`,
    borderRadius: borderRadius.sm,
    backgroundColor: `${colors.info}22`,
    color: colors.info,
    fontSize: typography.small.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
  } as React.CSSProperties,
  executedBadge: {
    display: 'inline-block',
    padding: `${spacing.xs} ${spacing.sm}`,
    borderRadius: borderRadius.sm,
    backgroundColor: colors.errorLight,
    color: colors.error,
    fontSize: typography.small.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
  } as React.CSSProperties,
  sampleTargets: {
    marginTop: spacing.sm,
  } as React.CSSProperties,
  sampleTargetList: {
    margin: `${spacing.xs} 0 0`,
    paddingLeft: spacing.lg,
    color: colors.textSecondary,
    fontSize: typography.small.fontSize,
  } as React.CSSProperties,
  backfillResultBox: {
    marginTop: spacing.md,
    padding: spacing.sm,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.sm,
    backgroundColor: colors.background,
  } as React.CSSProperties,
  error: {
    padding: spacing.md,
    borderRadius: borderRadius.sm,
    border: `1px solid ${colors.error}`,
    backgroundColor: `${colors.error}20`,
    color: colors.error,
  } as React.CSSProperties,
  overallBanner: {} as React.CSSProperties, // replaced by overallBannerStyle()
  overallBannerIcon: {
    fontSize: typography.h1.fontSize,
    fontWeight: typography.h1.fontWeight,
  } as React.CSSProperties,
  overallBannerTitle: {
    color: colors.text,
    fontSize: typography.body.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
  } as React.CSSProperties,
  overallBannerMeta: {
    color: colors.textSecondary,
    fontSize: typography.small.fontSize,
  } as React.CSSProperties,
  channelGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(440px, 1fr))',
    gap: spacing.md,
  } as React.CSSProperties,
  card: {
    padding: spacing.md,
    borderRadius: borderRadius.md,
    border: `1px solid ${colors.border}`,
    backgroundColor: colors.surface,
  } as React.CSSProperties,
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: spacing.sm,
    marginBottom: spacing.sm,
  } as React.CSSProperties,
  sectionTitle: {
    margin: 0,
    fontSize: typography.h2.fontSize,
    fontWeight: typography.h2.fontWeight,
    color: colors.text,
  } as React.CSSProperties,
  statusPill: {
    display: 'inline-block',
    padding: `2px ${spacing.sm}`,
    borderRadius: borderRadius.sm,
    fontSize: typography.small.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
    textTransform: 'uppercase',
    letterSpacing: '0.03em',
  } as React.CSSProperties,
  metaText: {
    color: colors.textSecondary,
    fontSize: typography.small.fontSize,
  } as React.CSSProperties,
  channelCard: {
    padding: spacing.md,
    borderRadius: borderRadius.md,
    border: `1px solid ${colors.border}`,
    backgroundColor: colors.surface,
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.md,
  } as React.CSSProperties,
  channelCardError: {
    borderColor: colors.error,
    backgroundColor: `${colors.error}08`,
  } as React.CSSProperties,
  channelCardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: spacing.sm,
    flexWrap: 'wrap',
  } as React.CSSProperties,
  channelCardTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.sm,
  } as React.CSSProperties,
  channelCardTitle: {
    margin: 0,
    fontSize: typography.h2.fontSize,
    fontWeight: typography.h2.fontWeight,
    color: colors.text,
  } as React.CSSProperties,
  channelPassBadge: {} as React.CSSProperties, // replaced by channelPassBadgeStyle()
  channelMeta: {
    color: colors.textSecondary,
    fontSize: typography.small.fontSize,
  } as React.CSSProperties,
  incompleteWarning: {
    color: colors.warning,
    fontWeight: typographyFlat.fontWeight.semibold,
  } as React.CSSProperties,
  errorTypeBadge: {
    display: 'inline-block',
    padding: `2px ${spacing.sm}`,
    borderRadius: borderRadius.sm,
    fontSize: typography.small.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
    backgroundColor: `${colors.error}20`,
    color: colors.error,
  } as React.CSSProperties,
  errorMessage: {
    margin: 0,
    color: colors.error,
    fontSize: typography.body.fontSize,
  } as React.CSSProperties,
  metricsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: spacing.sm,
  } as React.CSSProperties,
  metric: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.xs,
    padding: spacing.sm,
    borderRadius: borderRadius.sm,
    border: `1px solid ${colors.border}`,
    backgroundColor: colors.background,
  } as React.CSSProperties,
  metricLabel: {
    color: colors.textSecondary,
    fontSize: typography.small.fontSize,
  } as React.CSSProperties,
  metricValue: {
    fontSize: typography.h2.fontSize,
    fontWeight: typography.h2.fontWeight,
  } as React.CSSProperties,
  driftBreakdown: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: spacing.xs,
  } as React.CSSProperties,
  driftPill: {
    display: 'inline-block',
    padding: `2px ${spacing.sm}`,
    borderRadius: borderRadius.sm,
    fontSize: typography.small.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
  } as React.CSSProperties,
  sampleDriftSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.sm,
  } as React.CSSProperties,
  toggleButton: {
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.sm,
    backgroundColor: colors.surface,
    color: colors.text,
    padding: `${spacing.xs} ${spacing.sm}`,
    cursor: 'pointer',
    fontSize: typography.small.fontSize,
    alignSelf: 'flex-start',
  } as React.CSSProperties,
  tableWrap: {
    overflowX: 'auto',
  } as React.CSSProperties,
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: typography.small.fontSize,
  } as React.CSSProperties,
  th: {
    padding: `${spacing.xs} ${spacing.sm}`,
    borderBottom: `1px solid ${colors.border}`,
    textAlign: 'left',
    color: colors.textSecondary,
    fontWeight: typographyFlat.fontWeight.semibold,
    whiteSpace: 'nowrap',
  } as React.CSSProperties,
  td: {
    padding: `${spacing.xs} ${spacing.sm}`,
    borderBottom: `1px solid ${colors.border}`,
    color: colors.text,
  } as React.CSSProperties,
  tdStrong: {
    padding: `${spacing.xs} ${spacing.sm}`,
    borderBottom: `1px solid ${colors.border}`,
    color: colors.text,
    fontWeight: typographyFlat.fontWeight.semibold,
    maxWidth: '220px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  } as React.CSSProperties,
  emptyCell: {
    padding: `${spacing.sm} ${spacing.md}`,
    textAlign: 'center',
    color: colors.textSecondary,
    borderBottom: `1px solid ${colors.border}`,
  } as React.CSSProperties,
  driftTypePill: {
    display: 'inline-block',
    marginRight: spacing.xs,
    padding: `1px ${spacing.xs}`,
    borderRadius: borderRadius.sm,
    fontSize: typography.caption.fontSize,
    backgroundColor: `${colors.warning}20`,
    color: colors.warning,
    fontWeight: typographyFlat.fontWeight.semibold,
  } as React.CSSProperties,
  snapshotMeta: {
    color: colors.textSecondary,
    fontSize: typography.caption.fontSize,
  } as React.CSSProperties,
};
